import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  Pagination,
  IconButton,
  Alert,
  Button,
  Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import config from '../config';
import { color, radius, r } from '../theme/tokens';
import './Research.css';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DOMPurify from 'dompurify';
import { logAnalyticsEvent } from '../utils/analytics';
import { buildCitation } from '../utils/citation';
import { useAuth } from '../AuthContext';
import { ScienceIcon, LockOpenIcon, StudyStrengthIcon } from '../icons';

const specificConditionKeys = [
  'perimenopause',
  'infertility',
  'hashimotos_disease',
  'premenstrual_syndrome',
  'postpartum_depression',
  'urinary_tract_infection',
  'sarcopenia',
];

const EffectBars = ({ effect }) => {
  const totalBars = 5;
  
  // Define color schemes for different scores
  const colorSchemes = {
    1: ['#ef5350'], // Single red bar
    2: ['#ef5350', '#e57373'], // Two red bars
    3: ['#ffeb3b', '#fdd835', '#fbc02d'], // Three yellow bars
    4: ['#a5d6a7', '#81c784', '#66bb6a', '#4caf50'], // Four green bars
    5: ['#a5d6a7', '#81c784', '#66bb6a', '#4caf50', '#2e7d32'] // Five green bars with gradient
  };

  // Get the appropriate color scheme based on the score
  const activeColors = colorSchemes[Math.floor(effect)] || [];
  
  // Get the color of the last active bar for the number
  const lastActiveColor = activeColors[Math.floor(effect) - 1] || '#9e9e9e';

  return (
    <Box sx={{ 
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      borderLeft: '1px solid rgba(0,0,0,0.1)',
      paddingLeft: 1,
    }}>
      <Box sx={{ display: 'flex', gap: '0.125rem', alignItems: 'center' }}>
        {[...Array(totalBars)].map((_, index) => {
          const isActive = index < Math.floor(effect);

          return (
            <Box
              key={index}
              sx={{
                width: '0.25rem',
                height: '1rem',
                borderRadius: '0.125rem',
                backgroundColor: isActive
                  ? activeColors[index]
                  : 'rgba(0, 0, 0, 0.12)',
                transition: 'background-color 0.2s',
              }}
            />
          );
        })}
      </Box>
      <Typography
        component="span"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: lastActiveColor,
          ml: 0.5
        }}
      >
        {effect.toFixed(1)}
      </Typography>
    </Box>
  );
};

const StudyStrengthIndicator = ({ score, source }) => {
  const totalBars = 5;
  const activeScore = score || 0;
  const strengthColor = '#2e7d32';

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      backgroundColor: 'rgba(46, 125, 50, 0.04)',
      padding: '0.375rem 0.75rem',
      borderRadius: '0.5rem',
      border: '1px solid rgba(46, 125, 50, 0.1)',
    }}>
      <StudyStrengthIcon
        sx={{
          fontSize: '1rem',
          color: strengthColor,
          opacity: 0.7
        }}
      />
      <Box sx={{ display: 'flex', gap: '0.1875rem', alignItems: 'center' }}>
        {[...Array(totalBars)].map((_, index) => (
          <Box
            key={index}
            sx={{
              width: '1rem',
              height: '0.25rem',
              borderRadius: '0.125rem',
              backgroundColor: index < activeScore
                ? strengthColor
                : 'rgba(46, 125, 50, 0.12)',
              opacity: index < activeScore
                ? 0.7 + (index * 0.06)
                : 0.1,
              transition: 'all 0.2s ease',
            }}
          />
        ))}
      </Box>
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 500,
          color: strengthColor,
          opacity: 0.8,
          ml: 0.5
        }}
      >
        {activeScore.toFixed(1)}
      </Typography>
    </Box>
  );
};

const Research = ({ itemName, conditions, supplements }) => {
  const { supplementKey, conditionKey } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser, subscriptionStatus, isGrandfathered } = useAuth();
  const [researchArticles, setResearchArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [sortBy, setSortBy] = useState('study_strength');
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine if user has Pro access
  const hasProAccess = isGrandfathered || 
                       subscriptionStatus?.status === 'active' || 
                       subscriptionStatus?.status === 'trialing';
  // Debug mode state - defaults to OFF
  const [debugModeEnabled, setDebugModeEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('articlesDebugMode');
    return saved === 'true';
  });
  
  // Provider state - null means use production default
  const [provider, setProvider] = useState(() => {
    if (typeof window === 'undefined' || !debugModeEnabled) return null;
    const saved = localStorage.getItem('articlesProvider');
    return saved === 'gemini' ? 'gemini' : saved === 'openai' ? 'openai' : null;
  });

  // Define fetchResearchArticles first, before using it in refs
  const fetchResearchArticles = useCallback(async (page = 1, currentSort = sortBy, currentSearch = activeSearch) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        mode: 'list',
        page: page.toString(),
        pageSize: pagination.pageSize.toString(),
        sortBy: currentSort
      });

      if (supplementKey) {
        queryParams.append('supplementKey', supplementKey);
      } else if (conditionKey) {
        queryParams.append('conditionKey', conditionKey);
      }

      if (currentSearch) {
        queryParams.append('search', currentSearch);
      }

      // Only append provider if debug mode is enabled AND provider is set
      if (process.env.REACT_APP_ENVIRONMENT !== 'production' && debugModeEnabled && provider) {
        queryParams.append('provider', provider);
      }

      const response = await fetch(
        `${config.EDGE_FUNCTION_URL}/getResearchArticles?${queryParams}`,
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 416) {
          setResearchArticles([]);
          setPagination({
            page: 1,
            pageSize: 10,
            totalPages: 0,
            totalItems: 0,
            hasNextPage: false,
            hasPreviousPage: false
          });
          return;
        }

        if (response.status === 500) {
          throw new Error('Server error occurred. Please try again later.');
        }

        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.details || `HTTP error! status: ${response.status}`);
      }

      const { data, pagination: paginationData } = await response.json();
      
      if (!data) {
        throw new Error('No data received from server');
      }

      // Restrict to 3 articles for non-Pro users
      let displayData = data;
      let shouldShowUpgrade = false;
      
      if (!hasProAccess && data.length > 3) {
        displayData = data.slice(0, 3);
        shouldShowUpgrade = true;
      }

      setResearchArticles(displayData);
      setShowUpgradePrompt(shouldShowUpgrade);
      setPagination(paginationData);
      
      // Track when upgrade prompt is shown
      if (shouldShowUpgrade) {
        logAnalyticsEvent('research_upgrade_prompt_shown', {
          user_type: currentUser ? 'registered_free' : 'anonymous',
          supplement_key: supplementKey,
          condition_key: conditionKey,
          page: page
        });
      }
      
      // Log list view event only on initial load or when search/sort changes leading to a fetch
      if (page === 1) { // Assuming page 1 implies a new list view context
        logAnalyticsEvent('view_item_list', {
          item_list_id: supplementKey || conditionKey,
          item_list_name: itemName,
          item_category: supplementKey ? 'supplements' : 'conditions',
          item_list_context: 'research_tab', // Add context
          sort_by: currentSort,
          ...(currentSearch && { search_term: currentSearch }) // Include search term if present
        });
      }

    } catch (error) {
      console.error('Error fetching research articles:', error);
      setResearchArticles([]);
      setPagination({
        page: 1,
        pageSize: 10,
        totalPages: 0,
        totalItems: 0,
        hasNextPage: false,
        hasPreviousPage: false
      });
    } finally {
      setLoading(false);
    }
  }, [supplementKey, conditionKey, pagination.pageSize, sortBy, activeSearch, provider, debugModeEnabled, hasProAccess, itemName]);

  // Then add refs after the function is defined
  const searchParamsRef = React.useRef(searchParams);
  const setSearchParamsRef = React.useRef(setSearchParams);
  const fetchResearchArticlesRef = React.useRef(fetchResearchArticles);
  
  // Update refs when dependencies change
  useEffect(() => {
    searchParamsRef.current = searchParams;
    setSearchParamsRef.current = setSearchParams;
    fetchResearchArticlesRef.current = fetchResearchArticles;
  }, [searchParams, setSearchParams, fetchResearchArticles]);

  // First useEffect - now using refs
  useEffect(() => {
    // Check for stored state
    const storedState = sessionStorage.getItem('researchState');
    if (storedState) {
      const state = JSON.parse(storedState);
      
      // Create a new URLSearchParams object
      const newParams = new URLSearchParams();
      newParams.set('page', state.params.page);
      newParams.set('sortBy', state.params.sortBy || 'study_strength');
      if (state.params.search) {
        newParams.set('search', state.params.search);
      }
      setSearchParamsRef.current(newParams);
      
      // Update component state
      setSortBy(state.params.sortBy || 'study_strength');
      setSearchQuery(state.searchQuery || '');
      setActiveSearch(state.activeSearch || '');
      
      // Fetch with stored parameters
      fetchResearchArticlesRef.current(
        parseInt(state.params.page),
        state.params.sortBy || 'study_strength',
        state.activeSearch || ''
      ).then(() => {
        // Restore scroll position after content loads
        setTimeout(() => {
          window.scrollTo({
            top: state.scrollPosition,
            behavior: 'instant'
          });
        }, 100);
      });
      
      // Clear stored state
      sessionStorage.removeItem('researchState');
    } else {
      // Normal initialization from URL
      const page = parseInt(searchParamsRef.current.get('page')) || 1;
      const urlSort = searchParamsRef.current.get('sortBy') || 'study_strength';
      const urlSearch = searchParamsRef.current.get('search') || '';

      setSortBy(urlSort);
      setSearchQuery(urlSearch);
      setActiveSearch(urlSearch);
      fetchResearchArticlesRef.current(page, urlSort, urlSearch);
    }
  }, [supplementKey, conditionKey]); // Only depend on these keys

  // Second useEffect - now using refs
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    // Skip the first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const currentSearchParam = searchParamsRef.current.get('search') || '';
    const currentSortParam = searchParamsRef.current.get('sortBy') || 'study_strength';

    if (activeSearch !== currentSearchParam || sortBy !== currentSortParam) {
      // Create a new URLSearchParams object
      const newParams = new URLSearchParams();
      newParams.set('page', '1');
      newParams.set('sortBy', sortBy);
      if (activeSearch) {
        newParams.set('search', activeSearch);
      }
      setSearchParamsRef.current(newParams, { replace: true });
      fetchResearchArticlesRef.current(1, sortBy, activeSearch);
    }
  }, [activeSearch, sortBy]); // Only depend on these values

  // Trigger fetch when provider changes (dev/staging only)
  const isProviderInitialMount = React.useRef(true);
  useEffect(() => {
    if (isProviderInitialMount.current) {
      isProviderInitialMount.current = false;
      return;
    }

    if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
      // Reset to page 1 when provider changes
      const newParams = new URLSearchParams();
      newParams.set('page', '1');
      newParams.set('sortBy', sortBy);
      if (activeSearch) {
        newParams.set('search', activeSearch);
      }
      setSearchParamsRef.current(newParams, { replace: true });
      fetchResearchArticlesRef.current(1, sortBy, activeSearch);
    }
  }, [provider]);

  // Update handleArticleClick to store all relevant state and accept articleId
  const handleArticleClick = (clickedArticleId) => {
    const currentState = {
      params: {
        page: searchParams.get('page') || '1',
        sortBy: searchParams.get('sortBy') || 'study_strength',
        search: searchParams.get('search') || '',
      },
      scrollPosition: window.scrollY,
      activeSearch: activeSearch,
      searchQuery: searchQuery
    };
    sessionStorage.setItem('researchState', JSON.stringify(currentState));
    
    // Log article selection
    // Need article ID here. Assuming Link component passes article.article_ref somehow or we get it differently.
    // For now, let's log the context. We'll need to adjust this if handleArticleClick gets the ID.
    logAnalyticsEvent('click_read_more_article', {
      content_type: 'research_article',
      item_id: clickedArticleId, // Use the passed ID
      parent_item_id: supplementKey || conditionKey,
      parent_item_name: itemName,
      parent_item_category: supplementKey ? 'supplements' : 'conditions'
    });
  };

  const getArticlePath = (articleRef) => {
    const basePath = `/${supplementKey ? 'supplements' : 'conditions'}/${supplementKey || conditionKey}/research/${articleRef}`;
    if (process.env.REACT_APP_ENVIRONMENT !== 'production' && debugModeEnabled && provider) {
      return `${basePath}?provider=${provider}`;
    }
    return basePath;
  };

  // Restore scroll position when component mounts
  useEffect(() => {
    const savedPosition = sessionStorage.getItem('researchScrollPosition');
    if (savedPosition) {
      setTimeout(() => {
        window.scrollTo({
          top: parseInt(savedPosition, 10),
          behavior: 'instant'
        });
        sessionStorage.removeItem('researchScrollPosition');
      }, 100);
    }
  }, [researchArticles]); // Add researchArticles as dependency to ensure content is loaded

  // Citation helpers live in utils/citation.js (pure + unit-tested).

  // Update page change handler
  const handlePageChange = (event, newPage) => {
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
    fetchResearchArticles(newPage, sortBy, activeSearch);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Log pagination
    logAnalyticsEvent('click_pagination', {
      item_list_id: supplementKey || conditionKey,
      item_list_name: itemName,
      page_number: newPage
    });
  };

  // Update search handler
  const handleSearch = () => {
    if (searchQuery.trim()) {
      // Reset all parameters except the search
      searchParams.forEach((value, key) => {
        if (key !== 'search') {
          searchParams.delete(key);
        }
      });
      searchParams.set('page', '1');
      searchParams.set('sortBy', 'study_strength');
      searchParams.set('search', searchQuery.trim());
      setSearchParams(searchParams);
      
      // Update local state
      setActiveSearch(searchQuery.trim());
      setSortBy('study_strength');
      setPagination(prev => ({ ...prev, page: 1 }));
      
      // Fetch results
      fetchResearchArticles(1, 'study_strength', searchQuery.trim());
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Log search event
      logAnalyticsEvent('search', {
        search_term: searchQuery.trim(),
        item_list_id: supplementKey || conditionKey,
        item_list_name: itemName
      });
    }
  };

  // Update clear handler
  const handleClear = () => {
    // Reset all parameters
    searchParams.forEach((value, key) => {
      searchParams.delete(key);
    });
    searchParams.set('page', '1');
    searchParams.set('sortBy', 'study_strength');
    setSearchParams(searchParams);
    
    // Update local state
    setSearchQuery('');
    setActiveSearch('');
    setSortBy('study_strength');
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Fetch results
    fetchResearchArticles(1, 'study_strength', '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Log clear search event
    logAnalyticsEvent('clear_search', {
      item_list_id: supplementKey || conditionKey,
      item_list_name: itemName
    });
  };

  // Handler for sort clicks
  const handleSortChange = (newSortBy) => {
    if (newSortBy !== sortBy) {
      setSortBy(newSortBy);
      // Log sort event (fetch will happen via useEffect)
      logAnalyticsEvent('click_sort_option', {
        sort_by: newSortBy,
        item_list_id: supplementKey || conditionKey,
        item_list_name: itemName
      });
    }
  };

  // Add the sorting logic before rendering articles
  const sortedArticles = [...researchArticles].sort((a, b) => {
    if (sortBy === 'study_strength') {
      // Get study strength scores (0-5 range)
      const aScore = a.ai_study_strength?.score || 0;
      const bScore = b.ai_study_strength?.score || 0;
      
      // Sort by study strength only, no longer using effect size
      return bScore - aScore; // Sort descending
    }
    return 0;
  });

  // Simplified placeholder function using passed itemName
  const getSearchPlaceholder = () => {
    return itemName ? `Search ${itemName} articles...` : "Search articles...";
  };

  // Update the render function to use sortedArticles directly
  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      px: { xs: 2, sm: 3 },
    }}>
      {/* Debug Mode Banner */}
      {process.env.REACT_APP_ENVIRONMENT !== 'production' && debugModeEnabled && (
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2,
            mb: 1,
            maxWidth: r(1200),
            width: '100%',
            fontSize: '0.875rem',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem'
            }
          }}
        >
          🔧 Debug Mode Active - Using <strong>{provider === 'gemini' ? 'Gemini' : provider === 'openai' ? 'OpenAI' : 'Production Default'}</strong> provider
        </Alert>
      )}
      
      <Box sx={{ 
        width: '100%',
        maxWidth: r(1200),
      }}>
        {/* Search and controls section */}
        <Box sx={{ 
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', md: 'row' }, // Stack vertically on smaller screens
          alignItems: { xs: 'stretch', md: 'center' }, // Stretch on mobile, center on desktop
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 2, md: 3 }, // Consistent spacing between elements
          mt: 3,
          mb: 2,
        }}>
          {/* Search bar container */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', md: r(400) }, // Full width on mobile, fixed on desktop
            maxWidth: '100%',
            position: 'relative',
            backgroundColor: color.surface,
            borderRadius: radius.sm,
            border: `${r(1)} solid ${color.line}`,
            height: r(36),
            '&:hover': {
              backgroundColor: color.mintSoft,
              borderColor: alpha(color.ink, 0.18),
            },
            '&:focus-within': {
              backgroundColor: color.paper,
              borderColor: alpha(color.ink, 0.3),
              boxShadow: `0 0 0 ${r(1)} rgba(0,0,0,0.05)`,
            }
          }}>
            <SearchIcon sx={{ 
              ml: 1.25,
              color: 'rgba(0, 0, 0, 0.4)',
              fontSize: r(15)
            }} />
            <input
              type="text"
              placeholder={getSearchPlaceholder()}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                padding: `0 ${r(8)}`,
                fontSize: r(13),
                height: r(36),
                lineHeight: r(36),
                alignSelf: 'flex-start',
              }}
            />
            {searchQuery && (
              <IconButton
                onClick={handleClear}
                size="small"
                sx={{
                  mr: 0.5,
                  p: 0.5,
                  color: 'rgba(0, 0, 0, 0.4)',
                  '&:hover': {
                    color: 'rgba(0, 0, 0, 0.6)',
                    backgroundColor: 'transparent',
                  }
                }}
              >
                <ClearIcon sx={{ fontSize: r(14) }} />
              </IconButton>
            )}
          </Box>

          {/* Sort options */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: { xs: '100%', md: 'auto' }, // Full width on mobile, auto on desktop
            justifyContent: { xs: 'flex-start', md: 'flex-end' }, // Align left on mobile, right on desktop
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(0, 0, 0, 0.6)', 
                fontSize: { xs: r(11), sm: r(13) },
                whiteSpace: 'nowrap', // Prevent text wrapping
              }}
            >
              Sort by:
            </Typography>
            <Box sx={{ 
              display: 'flex',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderRadius: radius.sm,
              padding: r(2),
              border: `${r(1)} solid rgba(0, 0, 0, 0.08)`,
              width: 'auto', // Let it size to content
              flex: { xs: '0 1 auto', md: 'none' }, // Allow shrinking on mobile but don't grow
            }}>
              <Box
                onClick={() => handleSortChange('study_strength')}
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  py: 1,
                  fontSize: { xs: r(11), sm: r(13) },
                  cursor: 'pointer',
                  borderRadius: radius.sm,
                  backgroundColor: sortBy === 'study_strength' ? color.paper : 'transparent',
                  color: sortBy === 'study_strength' ? color.evergreen : 'rgba(0, 0, 0, 0.6)',
                  fontWeight: sortBy === 'study_strength' ? 500 : 400,
                  transition: 'all 0.2s ease',
                  boxShadow: sortBy === 'study_strength' ? `0 ${r(2)} ${r(4)} rgba(0,0,0,0.08)` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    backgroundColor: sortBy === 'study_strength' ? color.paper : 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                <StudyStrengthIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                Study Strength
              </Box>
              <Box
                onClick={() => handleSortChange('date')}
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  py: 1,
                  fontSize: { xs: r(11), sm: r(13) },
                  cursor: 'pointer',
                  borderRadius: radius.sm,
                  backgroundColor: sortBy === 'date' ? color.paper : 'transparent',
                  color: sortBy === 'date' ? color.evergreen : 'rgba(0, 0, 0, 0.6)',
                  fontWeight: sortBy === 'date' ? 500 : 400,
                  transition: 'all 0.2s ease',
                  boxShadow: sortBy === 'date' ? `0 ${r(2)} ${r(4)} rgba(0,0,0,0.08)` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    backgroundColor: sortBy === 'date' ? color.paper : 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                Publication Date
              </Box>
            </Box>

            {/* Debug Mode Toggle - dev/staging only */}
            {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}>
                {/* Debug Mode Switch */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(0, 0, 0, 0.6)', 
                      fontSize: { xs: r(11), sm: r(13) },
                      fontWeight: 500 
                    }}
                  >
                    Debug:
                  </Typography>
                  <input
                    type="checkbox"
                    checked={debugModeEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setDebugModeEnabled(enabled);
                      try {
                        localStorage.setItem('articlesDebugMode', enabled.toString());
                        if (!enabled) {
                          // Clear provider when disabling debug
                          localStorage.removeItem('articlesProvider');
                          setProvider(null);
                        }
                      } catch {}
                    }}
                    style={{
                      width: r(16),
                      height: r(16),
                      cursor: 'pointer',
                    }}
                  />
                </Box>

                {/* Provider Dropdown - only shown when debug enabled */}
                {debugModeEnabled && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: { xs: r(11), sm: r(13) } }}
                    >
                      Provider:
                    </Typography>
                    <Box
                      component="select"
                      value={provider || 'default'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const newProvider = value === 'default' ? null : value;
                        setProvider(newProvider);
                        try {
                          if (newProvider) {
                            localStorage.setItem('articlesProvider', newProvider);
                          } else {
                            localStorage.removeItem('articlesProvider');
                          }
                        } catch {}
                      }}
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: radius.sm,
                        border: `${r(1)} solid rgba(0,0,0,0.2)`,
                        backgroundColor: color.paper,
                        fontSize: { xs: r(11), sm: r(13) },
                      }}
                    >
                      <option value="default">Default (Production)</option>
                      <option value="openai">OpenAI</option>
                      <option value="gemini">Gemini</option>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Results count in its own container */}
        {!loading && researchArticles.length > 0 && (
          <Box sx={{ 
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 0,
            mt: 2,
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
            >
              {activeSearch 
                ? `Showing ${((pagination.page - 1) * pagination.pageSize) + 1}-${Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of ${pagination.totalItems} results`
                : `Showing ${((pagination.page - 1) * pagination.pageSize) + 1}-${Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of ${pagination.totalItems} articles`}
            </Typography>
          </Box>
        )}

        {/* Articles content */}
        <Box sx={{ 
          width: '100%',
          px: { xs: 0, sm: 0 },
          mt: 1,
          display: 'flex',
          justifyContent: 'center'
        }}>
          {/* Loading, Articles, or No Results */}
          {loading ? (
            <Box sx={{ textAlign: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : researchArticles.length > 0 ? (
            <Box sx={{ width: '100%' }}>
              {sortedArticles.map((article) => (
                <Paper
                  component={Link}
                  to={getArticlePath(article.article_ref)}
                  onClick={() => handleArticleClick(article.article_ref)}
                  key={article.id}
                  sx={{
                    display: 'block',
                    textDecoration: 'none',
                    marginBottom: 2,
                    padding: { xs: 2, sm: 2.5 },
                    transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
                    border: '0.0625rem solid',
                    borderColor: 'rgba(46,125,50,0.14)',
                    '&:hover': {
                      boxShadow: '0 0.5rem 1.25rem rgba(46,125,50,0.12)',
                      transform: 'translateY(-0.125rem)',
                      borderColor: 'rgba(46,125,50,0.3)',
                    },
                    '&:hover .listing-arrow': {
                      transform: 'translateX(0.25rem)',
                      color: color.accent,
                    },
                    width: '100%',
                    mx: { xs: 0, sm: 'auto' },
                    borderRadius: '0.875rem',
                    backgroundColor: color.paper,
                  }}
                >
                  {/* Simplified Title — the headline */}
                  <Typography
                    component="h3"
                    sx={{
                      mb: 0.75,
                      fontWeight: 700,
                      fontSize: { xs: '1.02rem', sm: '1.1rem' },
                      lineHeight: 1.35,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.ai_title_simplified || article.title) }}
                  />

                  {/* Academic title — muted attribution */}
                  {article.ai_title_simplified && article.title && (
                    <Typography
                      sx={{
                        mb: 1,
                        fontStyle: 'italic',
                        color: 'text.disabled',
                        fontSize: '0.78rem',
                        lineHeight: 1.45,
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.title) }}
                    />
                  )}

                  {/* Citation line — credibility */}
                  {buildCitation(article) && (
                    <Typography sx={{ mb: 1.25, color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.4 }}>
                      {buildCitation(article)}
                    </Typography>
                  )}

                  {/* Inline verdict + subtle directional affordance (whole card is a link) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(article.ai_study_strength?.score != null) && (
                      <StudyStrengthIndicator score={article.ai_study_strength.score} source={article.ai_study_strength?.source} />
                    )}
                    <ChevronRightIcon
                      className="listing-arrow"
                      aria-hidden
                      sx={{
                        ml: 'auto',
                        fontSize: '1.25rem',
                        color: alpha(color.accent, 0.85),
                        transition: 'transform 0.2s ease, color 0.2s ease',
                      }}
                    />
                  </Box>
                </Paper>
              ))}
              
              {/* Article count indicator for non-Pro users */}
              {showUpgradePrompt && pagination.totalItems > 3 && (
                <Box sx={{ 
                  textAlign: 'center', 
                  mt: 3,
                  mb: 2,
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.disabled',
                      fontWeight: 500,
                    }}
                  >
                    Showing 3 of {pagination.totalItems} articles
                  </Typography>
                </Box>
              )}
              
              {/* Upgrade Prompt for Non-Pro Users */}
              {showUpgradePrompt && (
                <Box sx={{
                  mt: 2,
                  mb: 4,
                  p: { xs: 3, sm: 4 },
                  textAlign: 'center',
                  bgcolor: alpha(color.accent, 0.05),
                  borderRadius: 2,
                  border: `${r(1)} solid ${alpha(color.accent, 0.18)}`,
                }}>
                  <LockOpenIcon sx={{ fontSize: '2rem', color: color.accent, mb: 1, opacity: 0.8 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    Research without the confusion.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', maxWidth: '26rem', mx: 'auto' }}>
                    Bias-vetted research. Clinical doses and timing.<br />Safety flags. In plain English.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 3, color: 'text.disabled' }}>
                    14-day free trial. Cancel anytime.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      logAnalyticsEvent('research_upgrade_clicked', {
                        user_type: currentUser ? 'registered_free' : 'anonymous',
                        supplement_key: supplementKey,
                        condition_key: conditionKey
                      });
                      navigate(currentUser ? '/subscription' : '/register');
                    }}
                    sx={{
                      minWidth: '11.25rem',
                      background: color.accent,
                      color: color.accentInk,
                      fontWeight: 600,
                      borderRadius: '0.75rem',
                      textTransform: 'none',
                      boxShadow: `0 0.125rem 0.5rem ${alpha(color.accent, 0.2)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: color.accentHover,
                        transform: 'translateY(-0.0625rem)',
                        boxShadow: `0 0.25rem 0.75rem ${alpha(color.accent, 0.25)}`,
                      }
                    }}
                  >
                    Start Free Trial
                  </Button>
                </Box>
              )}
              
              {/* Pagination - Only show for Pro users */}
              {pagination.totalPages > 1 && hasProAccess && (
                <Box sx={{ textAlign: 'center', my: 4 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{
              textAlign: 'center',
              alignItems: 'center',
              my: 4,
              mb: 6, // Increased bottom margin
              p: { xs: 2, sm: 3 },
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: radius.sm,
              border: `${r(1)} dashed rgba(0, 0, 0, 0.1)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: r(150),
              maxWidth: r(500), // Constrain width for better readability
              mx: 'auto' // Center the box itself if it's narrower than container
            }}>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.6 }}>
                {activeSearch
                  ? 'We couldn\'t find any articles matching your search.'
                  : specificConditionKeys.includes(conditionKey)
                  ? 'Getting the details just right for you! Our science sleuths are currently sifting through research for this supplement. Stay tuned – this page will be packed with info soon!'
                  : 'We don\'t have high-quality research on this topic yet.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Research;
export { StudyStrengthIndicator, EffectBars };
